import { Address_Is_PoBox, Addresses_Mailing_Parse } from "./address-parse";
import { Apn_Normalize } from "./apn";

const Street_Suffix_Canonical: Record<string, string> = {
  AV: "AVE",
  AVE: "AVE",
  AVENUE: "AVE",
  ST: "ST",
  STREET: "ST",
  RD: "RD",
  ROAD: "RD",
  DR: "DR",
  DRIVE: "DR",
  LN: "LN",
  LANE: "LN",
  CT: "CT",
  COURT: "CT",
  BLVD: "BLVD",
  BOULEVARD: "BLVD",
  WAY: "WAY",
  PL: "PL",
  PLACE: "PL",
  CIR: "CIR",
  CIRCLE: "CIR",
};

/** Common California cities for typo correction (esp. Bay Area / Contra Costa). */
const Ca_Cities: string[] = [
  "ALAMEDA",
  "ANTIOCH",
  "BAKERSFIELD",
  "BERKELEY",
  "BRENTWOOD",
  "BURBANK",
  "CARLSBAD",
  "CHICO",
  "CHULA VISTA",
  "CLAYTON",
  "CLOVIS",
  "CONCORD",
  "CORONA",
  "COSTA MESA",
  "DALY CITY",
  "DANVILLE",
  "DISCOVERY BAY",
  "DOWNEY",
  "EL CERRITO",
  "ELK GROVE",
  "ESCONDIDO",
  "FAIRFIELD",
  "FONTANA",
  "FREMONT",
  "FRESNO",
  "FULLERTON",
  "GARDEN GROVE",
  "GLENDALE",
  "HAYWARD",
  "HERCULES",
  "HUNTINGTON BEACH",
  "IRVINE",
  "LAFAYETTE",
  "LANCASTER",
  "LIVERMORE",
  "LODI",
  "LONG BEACH",
  "LOS ANGELES",
  "MARTINEZ",
  "MODESTO",
  "MORENO VALLEY",
  "MOUNTAIN VIEW",
  "NAPA",
  "NEWARK",
  "OAKLEY",
  "OAKLAND",
  "OCEANSIDE",
  "ONTARIO",
  "ORINDA",
  "OXNARD",
  "PALO ALTO",
  "PASADENA",
  "PITTSBURG",
  "PLEASANT HILL",
  "PLEASANTON",
  "POMONA",
  "RANCHO CORDOVA",
  "REDWOOD CITY",
  "RICHMOND",
  "RIVERSIDE",
  "ROSEVILLE",
  "SACRAMENTO",
  "SALINAS",
  "SAN BERNARDINO",
  "SAN DIEGO",
  "SAN FRANCISCO",
  "SAN JOSE",
  "SAN LEANDRO",
  "SAN MATEO",
  "SAN PABLO",
  "SAN RAFAEL",
  "SAN RAMON",
  "SANTA ANA",
  "SANTA CLARA",
  "SANTA CLARITA",
  "SANTA CRUZ",
  "SANTA ROSA",
  "SIMI VALLEY",
  "STOCKTON",
  "SUNNYVALE",
  "TEMECULA",
  "THOUSAND OAKS",
  "TORRANCE",
  "UNION CITY",
  "VALLEJO",
  "VENTURA",
  "VISALIA",
  "WALNUT CREEK",
  "WEST COVINA",
];

const Us_State_Names: Record<string, string> = {
  ALABAMA: "AL",
  ALASKA: "AK",
  ARIZONA: "AZ",
  ARKANSAS: "AR",
  CALIFORNIA: "CA",
  COLORADO: "CO",
  CONNECTICUT: "CT",
  DELAWARE: "DE",
  FLORIDA: "FL",
  GEORGIA: "GA",
  HAWAII: "HI",
  IDAHO: "ID",
  ILLINOIS: "IL",
  INDIANA: "IN",
  IOWA: "IA",
  KANSAS: "KS",
  KENTUCKY: "KY",
  LOUISIANA: "LA",
  MAINE: "ME",
  MARYLAND: "MD",
  MASSACHUSETTS: "MA",
  MICHIGAN: "MI",
  MINNESOTA: "MN",
  MISSISSIPPI: "MS",
  MISSOURI: "MO",
  MONTANA: "MT",
  NEBRASKA: "NE",
  NEVADA: "NV",
  "NEW HAMPSHIRE": "NH",
  "NEW JERSEY": "NJ",
  "NEW MEXICO": "NM",
  "NEW YORK": "NY",
  "NORTH CAROLINA": "NC",
  "NORTH DAKOTA": "ND",
  OHIO: "OH",
  OKLAHOMA: "OK",
  OREGON: "OR",
  PENNSYLVANIA: "PA",
  "RHODE ISLAND": "RI",
  "SOUTH CAROLINA": "SC",
  "SOUTH DAKOTA": "SD",
  TENNESSEE: "TN",
  TEXAS: "TX",
  UTAH: "UT",
  VERMONT: "VT",
  VIRGINIA: "VA",
  WASHINGTON: "WA",
  "WEST VIRGINIA": "WV",
  WISCONSIN: "WI",
  WYOMING: "WY",
  "DISTRICT OF COLUMBIA": "DC",
};

const Us_State_Codes = new Set(Object.values(Us_State_Names));

export type Search_Query_Kind = "apn" | "address";

export type Search_Query_Check = {
  ok: boolean;
  kind: Search_Query_Kind | null;
  /** Normalized query used for lookup. */
  query: string;
  /** Human-readable form (may include CA). */
  display: string;
  error?: string;
  /** Soft note when we auto-corrected typos / formatting. */
  correction?: string;
};

function Text_Whitespace_Collapse(Text: string): string {
  return Text.replace(/[,\s]+/g, " ").trim();
}

function Distance_Levenshtein(Left: string, Right: string): number {
  if (Left === Right) {
    return 0;
  }
  if (!Left.length) {
    return Right.length;
  }
  if (!Right.length) {
    return Left.length;
  }
  const Rows = Left.length + 1;
  const Cols = Right.length + 1;
  const Matrix: number[][] = Array.from({ length: Rows }, () => Array(Cols).fill(0));
  for (let I = 0; I < Rows; I++) {
    Matrix[I][0] = I;
  }
  for (let J = 0; J < Cols; J++) {
    Matrix[0][J] = J;
  }
  for (let I = 1; I < Rows; I++) {
    for (let J = 1; J < Cols; J++) {
      const Cost = Left[I - 1] === Right[J - 1] ? 0 : 1;
      Matrix[I][J] = Math.min(
        Matrix[I - 1][J] + 1,
        Matrix[I][J - 1] + 1,
        Matrix[I - 1][J - 1] + Cost,
      );
    }
  }
  return Matrix[Rows - 1][Cols - 1];
}

function City_Typo_Correct(City: string): string {
  const Upper = City.trim().toUpperCase();
  if (!Upper) {
    return "";
  }
  if (Ca_Cities.includes(Upper)) {
    return Upper;
  }

  let Best = "";
  let Best_Distance = Infinity;
  for (const Candidate of Ca_Cities) {
    const Max_Distance = Candidate.length <= 5 ? 1 : Candidate.length <= 8 ? 1 : 2;
    const Distance = Distance_Levenshtein(Upper, Candidate);
    if (Distance > 0 && Distance <= Max_Distance && Distance < Best_Distance) {
      Best = Candidate;
      Best_Distance = Distance;
    }
  }
  return Best;
}

function State_Code_From_Token(Token: string): string | null {
  const Upper = Token.trim().toUpperCase().replace(/\./g, "");
  if (!Upper) {
    return null;
  }
  if (Us_State_Codes.has(Upper)) {
    return Upper;
  }
  return Us_State_Names[Upper] || null;
}

/** Pull trailing US state from free text; returns remaining text + state code. */
export function Query_State_Extract(Text: string): { text: string; state: string | null } {
  let Working = Text_Whitespace_Collapse(Text);
  if (!Working) {
    return { text: "", state: null };
  }

  for (const Name of Object.keys(Us_State_Names).sort((A, B) => B.length - A.length)) {
    const Re = new RegExp(`\\b${Name.replace(/\s+/g, "\\s+")}\\b\\s*$`, "i");
    if (Re.test(Working)) {
      return {
        text: Working.replace(Re, "").trim(),
        state: Us_State_Names[Name],
      };
    }
  }

  const Code_Match = Working.match(/\b([A-Za-z]{2})\s*$/);
  if (Code_Match) {
    const Code = State_Code_From_Token(Code_Match[1]);
    if (Code) {
      return {
        text: Working.slice(0, Code_Match.index).trim(),
        state: Code,
      };
    }
  }

  return { text: Working, state: null };
}

function Query_Looks_Like_Apn(Text: string): boolean {
  const Digits = Apn_Normalize(Text);
  if (!/^\d{7,15}$/.test(Digits)) {
    return false;
  }
  // Pure digit / dashed / dotted APN — not a street address with house number + words.
  const Compact = Text.replace(/\s/g, "");
  return /^[\d\-./]+$/.test(Compact);
}

function Suffix_Token_Canonical(Token: string): string {
  const Key = Token.toUpperCase().replace(/\./g, "");
  return Street_Suffix_Canonical[Key] || Token;
}

function Address_Tokens_Normalize(Text: string): string {
  return Text_Whitespace_Collapse(Text)
    .split(" ")
    .map((Token) => Suffix_Token_Canonical(Token))
    .join(" ");
}

function Address_Display_Build(
  Street_Line: string,
  City: string,
  Zip: string,
): string {
  const Parts = [Street_Line, City, "CA"].filter(Boolean);
  let Line = Parts.join(", ");
  if (Zip) {
    Line = `${Line} ${Zip}`;
  }
  return Line;
}

/**
 * Normalize + validate APN / address search for California-only ParcelQ.
 * Auto-fixes light typos (city spelling, street suffix, whitespace).
 */
export function Search_Query_Check_Run(Raw: string): Search_Query_Check {
  const Trimmed = (Raw || "").trim();
  if (!Trimmed) {
    return {
      ok: false,
      kind: null,
      query: "",
      display: "",
      error: "Enter a California APN or street address.",
    };
  }

  if (Address_Is_PoBox(Trimmed)) {
    return {
      ok: false,
      kind: "address",
      query: Trimmed,
      display: Trimmed,
      error: "Use a street address or APN — PO boxes are not supported.",
    };
  }

  if (Query_Looks_Like_Apn(Trimmed)) {
    const Digits = Apn_Normalize(Trimmed);
    return {
      ok: true,
      kind: "apn",
      query: Digits,
      display: Digits,
    };
  }

  let Working = Text_Whitespace_Collapse(Trimmed);
  let Zip = "";
  const Zip_Match = Working.match(/\b(\d{5})(?:-\d{4})?\s*$/);
  if (Zip_Match) {
    Zip = Zip_Match[1];
    Working = Working.slice(0, Zip_Match.index).trim();
  }

  const { text: Without_State, state } = Query_State_Extract(Working);
  Working = Without_State;

  if (state && state !== "CA") {
    return {
      ok: false,
      kind: "address",
      query: Trimmed,
      display: Trimmed,
      error: "ParcelQ covers California parcels only. Enter a CA address or APN.",
    };
  }

  if (Zip && !Zip.startsWith("9")) {
    return {
      ok: false,
      kind: "address",
      query: Trimmed,
      display: Trimmed,
      error: "That ZIP is outside California. ParcelQ is California only.",
    };
  }

  Working = Address_Tokens_Normalize(Working);
  if (Working.length < 5) {
    return {
      ok: false,
      kind: "address",
      query: Working,
      display: Working,
      error: "Enter a fuller California address (e.g. 176 Continente Ave, Brentwood) or an APN.",
    };
  }

  const Parsed = Addresses_Mailing_Parse(
    [Working, state === "CA" ? "CA" : "", Zip].filter(Boolean).join(" "),
  );

  let City = Parsed.city;
  let Correction_Note = "";
  if (City) {
    const Fixed_City = City_Typo_Correct(City);
    if (Fixed_City && Fixed_City !== City) {
      Correction_Note = `Corrected city “${City}” → “${Fixed_City}”.`;
      City = Fixed_City;
    }
  } else {
    // City glued without clear parse — try last 1–3 tokens as city typo.
    const Tokens = Working.split(/\s+/);
    for (const N of [3, 2, 1]) {
      if (Tokens.length <= N + 1) {
        continue;
      }
      const Candidate = Tokens.slice(-N).join(" ");
      const Fixed = City_Typo_Correct(Candidate);
      if (Fixed) {
        if (Fixed !== Candidate.toUpperCase()) {
          Correction_Note = `Corrected city “${Candidate}” → “${Fixed}”.`;
        }
        City = Fixed;
        Working = Tokens.slice(0, -N).join(" ");
        break;
      }
    }
  }

  const Street =
    Parsed.street_line && Parsed.city
      ? [
          Parsed.street_number,
          Parsed.street_name,
          Parsed.suffix ? Suffix_Token_Canonical(Parsed.suffix) : "",
        ]
          .filter(Boolean)
          .join(" ")
      : Working;

  if (!Street || Street.length < 3) {
    return {
      ok: false,
      kind: "address",
      query: Working,
      display: Working,
      error: "Enter a California street address with house number, or an APN.",
    };
  }

  // City-only / street-name-only without number is usually too vague.
  if (!Parsed.street_number && !/^\d/.test(Street) && !City) {
    return {
      ok: false,
      kind: "address",
      query: Street,
      display: Street,
      error: "Add a house number and city (California), or search by APN.",
    };
  }

  const Query_Parts = [Street, City, "CA", Zip].filter(Boolean);
  const Query = Query_Parts.join(" ");
  const Display = Address_Display_Build(Street, City, Zip);

  const Original_Norm = Text_Whitespace_Collapse(Trimmed).toUpperCase();
  const Display_Norm = Text_Whitespace_Collapse(Display).toUpperCase();
  const Changed =
    Original_Norm !== Display_Norm &&
    Original_Norm.replace(/,/g, "") !== Display_Norm.replace(/,/g, "");

  return {
    ok: true,
    kind: "address",
    query: Query,
    display: Display,
    correction: Correction_Note || (Changed ? `Using: ${Display}` : undefined),
  };
}
